import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'Shared/util/custom-hooks';
import { joinClassNames as c } from 'Shared/util/styles';
import Button from 'Shared/components/button';
import InlineSvg from 'Shared/components/inline-svg';
import Spinner from 'Shared/components/spinner';
import styles from './drag-and-drop.css';


const preventAndStopEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const validateFiles = (files, acceptedFileTypes) => files.reduce((output, file) => {
  const hasAcceptedFileType = (Array.isArray(acceptedFileTypes) && acceptedFileTypes.length > 0)
    ? acceptedFileTypes.some((fileType) => file.name.trim().toLowerCase().endsWith(fileType.trim().toLowerCase()))
    : true;

  if (hasAcceptedFileType) {
    output.accepted.push(file);
  } else {
    output.notAccepted.push(file);
  }
  return output;
}, { accepted: [], notAccepted: [] });


const DragAndDrop = ({
  onFileUpload,
  acceptedFileTypes,
  disabled,
  loading,
  className,
}) => {
  const [isActiveDropZone, setIsActiveDropZone] = useState(false);
  const fileUploadRef = useRef();
  const t = useTranslation(x => x.drag_and_drop);

  const handleDragEnter = (e) => {
    preventAndStopEvent(e);
    if (disabled || loading) return;
    setIsActiveDropZone(true);
  };

  const handleDragLeave = (e) => {
    preventAndStopEvent(e);
    if (disabled || loading) return;
    setIsActiveDropZone(false);
  };

  const handleDragOver = (e) => preventAndStopEvent(e);

  const handleDrop = (e) => {
    preventAndStopEvent(e);
    if (disabled || loading) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const validFiles = validateFiles([...e.dataTransfer.files], acceptedFileTypes);
      onFileUpload(validFiles);
    }
    setIsActiveDropZone(false);
  };

  const handleFileUpload = (e) => {
    if (disabled || loading) return;
    if (e.target.files && e.target.files.length > 0) {
      const validFiles = validateFiles([...e.target.files], acceptedFileTypes);
      onFileUpload(validFiles);
      e.target.value = ''; // The field must be reset to upload the same file twice in a row
    }
  };

  const handleFileUploadButtonClick = () => ((fileUploadRef && fileUploadRef.current) && fileUploadRef.current.click());

  return (
    <div className={c(styles.container, className)}>
      <div
        className={styles.dropZoneOverlay}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />
      {loading && <Spinner customClass={styles.spinner} />}
      <div className={c(styles.dropZoneContainer, (isActiveDropZone && styles.active), (disabled && styles.disabled), (loading && styles.loading))}>
        <div className={c(styles.uploadContainer)}>
          <div className={styles.dropContainer}>
            <InlineSvg src="/upload-cloud.svg" />
            <span className={styles.uploadText1}>{t.drag_and_drop}</span>
            <span className={styles.uploadText2}>{t.your_files}</span>
            <input
              ref={fileUploadRef}
              type="file"
              accept={acceptedFileTypes && acceptedFileTypes.join()}
              multiple
              onChange={handleFileUpload}
            />
          </div>
          <div className={styles.middleText}>
            {t.or}
          </div>
          <div>
            <Button
              buttonType="button-primary"
              onClick={handleFileUploadButtonClick}
              text={t.choose_files}
              attributes={{ disabled: (isActiveDropZone || disabled) }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

DragAndDrop.propTypes = {
  onFileUpload: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default DragAndDrop;
